import Select from '@components/v2/form/Select/Select';
import ListItemText from '@components/v2/ui/List/ListItemText';
import MenuItem from '@components/v2/ui/Menu/MenuItem';
import Skeleton from '@components/v2/ui/Skeleton/Skeleton';
import { Box, Stack, StackProps, Typography } from '@mui/material';
import LangEnum from '../../../../../../../../lang/LangEnum';
import { OrderConditionsTestIds } from '../../../../../../../../utils/testIds/OrderConditions.testIds';
import { useTranslation } from '../../../../../../../translation/TranslationContext';
import { OrderConditionFreightFieldViewProps } from './orderConditionFreightField.types';
import { getFreightOptionSupportText } from './orderConditionFreightField.utils';

const EMPTY_VALUE_PLACEHOLDER = '\u00a0';

const LOADING_OPTIONS_COUNT = 3;
const LOADING_OPTION_HEIGHT = 46;
const LOADING_OPTIONS_GAP = '2px';

const MenuBlock = ({ children, sx }: Pick<StackProps, 'children' | 'sx'>) => (
  <Stack sx={sx}>{children}</Stack>
);

MenuBlock.muiSkipListHighlight = true;

const OrderConditionFreightFieldView = ({
  options,
  selectedOptionId,
  selectedLabel,
  isDisabled,
  isLoading,
  hasError,
  hasQuoteError,
  canEnterManually,
  errorMessage,
  onBlur,
  onOpen,
  onSelect,
  onRequestManualEntry,
}: OrderConditionFreightFieldViewProps) => {
  const { translate } = useTranslation();

  const renderSelectedValue = () => (
    <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
      {selectedLabel ?? EMPTY_VALUE_PLACEHOLDER}
    </Box>
  );

  const renderErrorOptions = () => {
    if (!canEnterManually) {
      return <MenuItem disabled>{translate(LangEnum.GLOBAL_NO_OPTIONS)}</MenuItem>;
    }

    return (
      <MenuItem key="manual-entry" onClick={onRequestManualEntry}>
        <Typography variant="body2">
          {translate(LangEnum.ORDER_CONDITION_FREIGHT_MANUAL_ENTRY)}
        </Typography>
      </MenuItem>
    );
  };

  const renderOptions = () => {
    if (isLoading) {
      return (
        <MenuBlock sx={{ gap: LOADING_OPTIONS_GAP }}>
          {Array.from({ length: LOADING_OPTIONS_COUNT }, (_, index) => (
            <Skeleton
              key={`loading-option-${index}`}
              variant="rounded"
              width="100%"
              height={LOADING_OPTION_HEIGHT}
              sx={(theme) => ({
                borderRadius: `${theme.shape.borderRadiusMd}px`,
                backgroundColor: theme.palette.background.contrast,
              })}
            />
          ))}
        </MenuBlock>
      );
    }

    if (hasQuoteError) {
      return renderErrorOptions();
    }

    if (!options.length) {
      return <MenuItem disabled>{translate(LangEnum.GLOBAL_NO_OPTIONS)}</MenuItem>;
    }

    return options.map((option) => (
      <MenuItem key={option.id} value={option.id} disabled={!option.isValid}>
        <Box sx={{ display: 'flex', gap: 2, width: '100%', justifyContent: 'space-between' }}>
          <ListItemText
            primary={option.companyName ?? option.methodName}
            secondary={
              <>
                {getFreightOptionSupportText(option)}
                {option.invalidReason && (
                  <Box
                    component="span"
                    sx={{ display: 'block', color: 'error.main', whiteSpace: 'normal' }}
                  >
                    {option.invalidReason}
                  </Box>
                )}
              </>
            }
          />
          <ListItemText
            primary={option.priceLabel}
            slotProps={{ primary: { sx: { textAlign: 'end' } } }}
          />
        </Box>
      </MenuItem>
    ));
  };

  return (
    <Select
      displayEmpty
      label={translate(LangEnum.GLOBAL_SHIPPING_COST)}
      value={selectedOptionId}
      disabled={isDisabled}
      error={hasError || Boolean(errorMessage)}
      helperText={errorMessage}
      testId={OrderConditionsTestIds.FREIGHT_QUOTE_FIELD}
      renderValue={renderSelectedValue}
      onBlur={onBlur}
      onOpen={onOpen}
      onChange={(event) => onSelect(String(event.target.value))}
    >
      {renderOptions()}
    </Select>
  );
};

export default OrderConditionFreightFieldView;
